import { AskConfirmation, buttonsYesNo } from '../../../webmodule-common/other/ui/modal-confirmation';
import { Branch, ViewBranchUser } from '../../api/dealer-api-interface-franchisee';
import {
  canClose,
  ErrorAbandon,
  isAutoSaving,
  SavePromptOptions,
  saveWithIndicator
} from '../../../webmodule-common/other/save-workflow';
import { checkValidations, throwValidations } from '../../../webmodule-common/other/ui/data-entry-screen-helpers';
import { customElement, property, query } from 'lit/decorators.js';
import { DataProvider, ProviderBackup } from '../../../webmodule-common/other/data-provider';
import { DevelopmentError, showDevelopmentError } from '../../../webmodule-common/other/development-error';
import { emptyAddress, singleLineAddress } from '../../../webmodule-common/other/ui/maps/map-helpers';
import { emptyGuid, guidEquals } from '../../../webmodule-common/other/api/guid';
import { franchiseeDataAdaptor } from '../data/franchisee-data-adaptor';
import { getCurrentUser } from '../../../webmodule-common/other/api/current-user';
import { glossary } from '../../../localization/global';
import { html, TemplateResult } from 'lit';
import { isEmptyOrSpace, validId } from '../../../webmodule-common/other/ui/string-helper-functions';
import { IUserClaims, userClaims } from '../../../webmodule-common/other/currentuser-claims';
import { LangFranchiseeBranchEditor } from '../../../localization/scripts/franchisee/editors/franchisee-branch-editor-lang';
import { LitBaseModal } from '../../../webmodule-common/other/ui/modal/modal-factory-lit';
import { LitElementLangBase } from '../../../webmodule-common/other/litelement-base';
import { localDateToServer } from '../../../webmodule-common/other/datetime-converter';
import { userDataStore } from '../../dealer-franchisee/common/current-user-data-store';
import { userSecurity } from '../../../webmodule-common/other/api/user-security';
import { validateHideBranch } from '../../dealer-franchisee/common/branch-access';
import { WebModuleFranchiseeBranchEditor } from './franchisee-branch-editor';
import { WebModuleTable, WebModuleTableColumnDef } from '../../../webmodule-common/components/src/webmodule-components';

@customElement('wm-franchisee-branches-editor')
export class WebModuleFranchiseeBranchesEditor extends LitElementLangBase {
  @query('#branches-table')
  table?: WebModuleTable;
  @property()
  franchiseeId = '';
  @property() filterValue = '';
  @property() filterActive = '';
  @property() readonly = false;
  /**
   * Per-branch users from GetBranches.
   * Used to block deactivating a branch that still has single-branch users.
   * Multi-branch users appear on every branch key and are excluded by validation.
   */
  @property({ attribute: false }) branchUsers?: { [key: string]: ViewBranchUser[] };
  claims: IUserClaims = userClaims(getCurrentUser()?.id ?? emptyGuid, userSecurity());

  private _data?: Branch[] | undefined;

  protected get data(): Branch[] | undefined {
    return this._data;
  }

  @property({ type: Array })
  protected set data(value: Branch[] | undefined) {
    this._data = value;
    this.table?.fetchEvent();
  }

  render() {
    const keyEvent = (item: Branch) => {
      return item.id;
    };
    const columns: WebModuleTableColumnDef[] = [];
    columns.push({
      title: () => LangFranchiseeBranchEditor.branchName(),
      minWidth: '280px',
      fieldName: 'name',
      fade: true,
      // Item is of type Branch
      displayValue: (_table: WebModuleTable, item) => {
        const branch = item as Branch;

        const clickItem = (e: Event) => {
          e.stopPropagation();
          e.preventDefault();
          this.editBranch(branch);
        };
        return html`<a href="#" @click=${clickItem}>${branch.name}</a>`;
      }
    });
    columns.push({
      title: () => LangFranchiseeBranchEditor.abbreviatedName(),
      minWidth: '160px',
      fieldName: 'abbreviatedName',
      displayValue: (_table: WebModuleTable, item) => {
        const branch = item as Branch;
        return branch.abbreviation;
      }
    });
    columns.push({
      title: () => LangFranchiseeBranchEditor.physicalAddress(),
      minWidth: '300px',
      fieldName: 'physicalAddress',
      fade: true,
      displayValue: (_table: WebModuleTable, item) => {
        const branch = item as Branch;
        return singleLineAddress(branch.physicalAddress);
      }
    });
    columns.push({
      title: () => LangFranchiseeBranchEditor.branchStatus(),
      width: '160px',
      align: 'center',
      fieldName: 'hidden',
      displayValue: (_table: WebModuleTable, item) => {
        const branch = item as Branch;
        // Active = success; deactivated (hidden) = danger.
        return branch.hidden
          ? html`<webmodule-tag size="small" variant="danger"
              >${LangFranchiseeBranchEditor.statusDeactivated()}</webmodule-tag
            >`
          : html`<webmodule-tag size="small" variant="success"
              >${LangFranchiseeBranchEditor.statusActive()}</webmodule-tag
            >`;
      }
    });

    return html` <div class=" branches-editor">
      <webmodule-table
        id="branches-table"
        .keyevent=${keyEvent}
        .elevated=${false}
        .cellBorders=${false}
        .uppercaseHeader=${false}
        .columns=${columns}
        pageLength="14"
        @fetch=${(e: CustomEvent) => this.internalDataLoad(e)}
        .clickrows=${false}
      >
      </webmodule-table>
    </div>`;
  }

  async editBranch(item: Branch) {
    const d = new BranchEditDialog(item, this.readonly, this.data, this.branchUsers);
    await d.showModal();
    if (d.currentBranch.recordVersion !== item.recordVersion) {
      this.branchChangedEvent(d.currentBranch);
    }
  }

  dispatchCustom(name: string, values: object) {
    const options = {
      detail: { ...values },
      bubbles: true,
      composed: true
    };
    //wm-be webmodule-brancheditor
    this.dispatchEvent(new CustomEvent(`wm-be-${name}`, options));
  }

  connectedCallback(): void {
    super.connectedCallback();
  }

  disconnectedCallback(): void {
    super.disconnectedCallback();
  }

  branchChangedEvent(branch: Branch) {
    this.dispatchCustom('changed', {
      branch: branch
    });
  }

  protected createRenderRoot(): HTMLElement | DocumentFragment {
    return this;
  }

  private matchFilter(vbl: Branch): boolean {
    function matches(a: string, b: string) {
      return a === '' || a.toLowerCase().includes(b);
    }

    if (this.claims.isSingleBranchManager && !guidEquals(vbl.id, this.claims.branchId)) return false;
    if (this.claims.isBasicUser && !guidEquals(vbl.id, this.claims.branchId)) return false;
    return matches(this.filterValue, vbl.name);
  }

  /** This is called by the table when it wants new page data */
  private async internalDataLoad(e: CustomEvent) {
    // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
    const table = e.detail.table as WebModuleTable;
    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
    const index = (e.detail?.pageIndex as number) ?? 0;
    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
    const length = (e.detail?.pageLength as number) ?? 1000;
    if (!this.data) {
      table.data = [];
    }
    if (this.data) {
      const filtered = this.data.filter(x => this.matchFilter(x));
      table.data = filtered.slice(index * length, index * length + length);
      table.rowCount = filtered.length;
    } else table.data = [];
  }
}

class BranchEditDialog extends LitBaseModal {
  backupBranch: ProviderBackup<Branch>;
  abandoned: any;
  branch: Branch;
  readonly: boolean;
  allBranches?: Branch[];
  branchUsers?: { [key: string]: ViewBranchUser[] };

  constructor(
    branch: Branch,
    readonly: boolean,
    allBranches?: Branch[],
    branchUsers?: { [key: string]: ViewBranchUser[] }
  ) {
    super();
    this.branch = branch;
    this.readonly = readonly;
    this.allBranches = allBranches;
    this.branchUsers = branchUsers;
    this.backupBranch = new ProviderBackup<Branch>(
      new DataProvider<Branch>(
        () => this.branch,
        async b => {
          const newBranch = await franchiseeDataAdaptor().setBranch(b);
          if (newBranch) this.branch = newBranch;
          return newBranch;
        }
      )
    );
  }

  get currentBranch(): Branch {
    if (!this.backupBranch) throw new DevelopmentError('current branch does not exist');
    return this.backupBranch?.item;
  }

  get isCreating(): boolean {
    return !validId(this.currentBranch.id);
  }

  get isFooterVisible(): boolean {
    return true;
  }

  protected get modalSize() {
    return 'modal-fullscreen';
  }

  title(): string | TemplateResult {
    return this.isCreating ? LangFranchiseeBranchEditor.addBranch() : LangFranchiseeBranchEditor.editBranch();
  }

  public getValidationErrors(): string[] {
    if (this.readonly) return [];
    const errors: string[] = [];

    if (isEmptyOrSpace(this.currentBranch.taxRegistrationNumber)) {
      errors.push(LangFranchiseeBranchEditor.provideTaxRegistrationNumber());
    }

    if (isEmptyOrSpace(this.currentBranch.name)) {
      errors.push(LangFranchiseeBranchEditor.provideBranchName());
    }

    if (isEmptyOrSpace(this.currentBranch.abbreviation)) {
      errors.push(LangFranchiseeBranchEditor.provideBranchAbbreviatedName());
    }

    // Deactivate/hide validation (permission, last active branch, single-branch users).
    for (const issue of validateHideBranch({
      isCreating: this.isCreating,
      currentHidden: this.currentBranch.hidden,
      originalHidden: this.backupBranch.provider.value.hidden,
      canChangeHidden: userDataStore.canChangeBranchHidden,
      allBranches: this.allBranches,
      currentBranchId: this.currentBranch.id,
      branchUsers: this.branchUsers
    })) {
      if (issue.kind === 'permission') errors.push(LangFranchiseeBranchEditor.cannotChangeBranchHidden());
      if (issue.kind === 'last-active') errors.push(LangFranchiseeBranchEditor.lastBranchCannotBeHidden());
      if (issue.kind === 'single-branch-users') {
        errors.push(LangFranchiseeBranchEditor.cannotHideBranchWithSingleBranchUsers(issue.count));
      }
    }

    const physicalLine1 = this.currentBranch.physicalAddress.line1;
    const physicalPostCode = this.currentBranch.physicalAddress.postcode;

    if (isEmptyOrSpace(physicalLine1)) {
      errors.push(LangFranchiseeBranchEditor.provideStreetAddress());
    }

    if (isEmptyOrSpace(physicalPostCode)) {
      errors.push(LangFranchiseeBranchEditor.provideZipCode());
    }

    if (this.currentBranch.billingAddress) {
      const billingLine1 = this.currentBranch.billingAddress?.line1;
      const billingPostCode = this.currentBranch.billingAddress?.postcode;

      if (isEmptyOrSpace(billingLine1)) errors.push(LangFranchiseeBranchEditor.provideBillingStreetAddress());

      if (isEmptyOrSpace(billingPostCode)) errors.push(LangFranchiseeBranchEditor.provideBillingAddressPostcode());
    }
    return errors;
  }

  updateCurrentBranch(branch: Branch) {
    this.backupBranch.pushChangedItem(branch);
  }

  bodyTemplate(): TemplateResult {
    const branchChangedEvent = (e: CustomEvent<{ branch: Branch }>) => {
      e.stopImmediatePropagation();
      const be = e.currentTarget as WebModuleFranchiseeBranchEditor;
      if (be.branch) this.updateCurrentBranch(be.branch);
    };
    return html` <wm-franchisee-branch-editor
      .branch=${this.currentBranch}
      .isCreating=${this.isCreating}
      @wm-be-changed=${branchChangedEvent}
      .readonly=${this.readonly}
    ></wm-franchisee-branch-editor>`;
  }

  /**
   * Confirm before saving an activate/deactivate (Hidden) change.
   * Returns false if the user cancels. Invoked on Save/Update only.
   */
  private async confirmHiddenChangeIfNeeded(): Promise<boolean> {
    if (this.isCreating) return true;

    const originalHidden = this.backupBranch.provider.value.hidden;
    const nextHidden = this.currentBranch.hidden;
    if (originalHidden === nextHidden) return true;

    const message = nextHidden
      ? LangFranchiseeBranchEditor.confirmDeactivateBranch()
      : LangFranchiseeBranchEditor.confirmActivateBranch();
    const title = nextHidden
      ? LangFranchiseeBranchEditor.confirmDeactivateBranchTitle()
      : LangFranchiseeBranchEditor.confirmActivateBranchTitle();

    return await AskConfirmation(message, buttonsYesNo(), undefined, title);
  }

  async performAutoSave(options: SavePromptOptions): Promise<boolean> {
    //if we dont need to save, then just exit.
    //but if we are not in autosave mode, we are going to force a save.
    let needsSave = false;
    try {
      needsSave = await options.needsSaveEvent();
    } catch (e) {
      await showDevelopmentError(e as Error);
      return false;
    }

    //this is to let us abandon new items that have had no editing
    // eslint-disable-next-line @typescript-eslint/no-throw-literal
    if (!needsSave && this.isCreating) throw new ErrorAbandon('Cancel', LangFranchiseeBranchEditor.cancelNewEdit());

    throwValidations(this.getValidationErrors());

    if (isAutoSaving() && !needsSave) return true;

    // Confirm activate/deactivate changes before saving.
    if (!(await this.confirmHiddenChangeIfNeeded())) return false;

    //this is our basic save indicator
    return await saveWithIndicator(async () => await this.internalSaveData());
  }

  async internalSaveData() {
    const b = await this.backupBranch.commitChanges();
    return b !== null;
  }

  getAutoSavePromptOptions(): SavePromptOptions {
    return {
      isReadonly: this.readonly,
      autoSaveEvent: async options => {
        return await this.performAutoSave(options);
      },
      dictionaryName: glossary('branch'),
      needsSaveEvent: async () => this.backupBranch.changed,
      abandonSaveEvent: async () => {
        this.abandoned = true;
        this.backupBranch.reset();
        return true;
      },
      displaySaveModal: false,
      informationDispatcher: undefined
    };
  }

  async canClose(): Promise<boolean> {
    return this.abandoned || (await canClose(this.getAutoSavePromptOptions()));
  }

  footerTemplate(): TemplateResult | null | undefined {
    const saveEvent = async () => {
      if (await this.canClose()) {
        await this.hideModal();
      }
    };

    return html`
      <webmodule-button size="small" variant="default" @click=${() => this.hideModal()}
        >${LangFranchiseeBranchEditor.close()}
      </webmodule-button>
      <webmodule-button size="small" variant="primary" @click=${() => saveEvent()}
        >${LangFranchiseeBranchEditor.saveBranch()}
      </webmodule-button>
    `;
  }

  protected async checkValidations(): Promise<boolean> {
    const errors = this.getValidationErrors();
    return await checkValidations(errors);
  }
}

export async function createBranch(): Promise<Branch | undefined> {
  const newBranch: Branch = {
    abbreviation: '',
    taxRegistrationNumber: '',
    billingAddress: null,
    franchiseeId: franchiseeDataAdaptor().getFranchisee().id,
    id: emptyGuid,
    name: '',
    physicalAddress: emptyAddress(),
    physicalAddressAsDefaultShipping: false,
    dateCreated: localDateToServer(new Date()),
    recordVersion: '',
    shippingNotes: '',
    hidden: false
  };
  const d = new BranchEditDialog(newBranch, false);
  await d.showModal();
  if (d.currentBranch.recordVersion !== newBranch.recordVersion) {
    return d.currentBranch;
  }
  return undefined;
}
